// src/features/auth/OTPVerify.jsx
import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Clock, RefreshCw, AlertTriangle, UserCheck, ShieldAlert } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/shared/components/ui/button';
import { toast } from 'sonner';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 30; // seconds

// ─── Reason metadata ─────────────────────────────────────────────────────────
const REASON_META = {
  FIRST_LOGIN: {
    label: 'First-time verification',
    description: "Please verify your email address to activate your account.",
    Icon: UserCheck,
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    iconColor: 'text-blue-500',
  },
  RISK_TRIGGERED: {
    label: 'Unusual activity detected',
    description: "We noticed a new device or location. Please verify it's you.",
    Icon: AlertTriangle,
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    iconColor: 'text-amber-500',
  },
  ADMIN_2FA: {
    label: 'Admin two-factor authentication',
    description: 'Two-factor authentication is required for all admin accounts.',
    Icon: ShieldAlert,
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-800',
    iconColor: 'text-purple-500',
  },
};

// ─── Resend countdown hook ────────────────────────────────────────────────────
function useResendCooldown(initialSeconds = RESEND_COOLDOWN) {
  const [seconds, setSeconds] = useState(initialSeconds);
  const timerRef = useRef(null);

  const start = useCallback(() => {
    setSeconds(initialSeconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) { clearInterval(timerRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  }, [initialSeconds]);

  useEffect(() => { start(); return () => clearInterval(timerRef.current); }, [start]);

  return { seconds, canResend: seconds === 0, restart: start };
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function OTPVerify() {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    verifyOTP,
    cancelOTP,
    resendOTP,
    otpEmail,
    otpPending,
    otpReason,
    loading: authLoading,
    error: authError,
    clearError,
    isAuthenticated,
    user,
  } = useAuth();

  const { seconds, canResend, restart: restartCooldown } = useResendCooldown();

  // Guard: if OTP session vanishes, send back to login
  useEffect(() => {
    if (!otpPending) navigate('/login', { replace: true });
  }, [otpPending, navigate]);

  // After successful verify, redirect by role
  useEffect(() => {
    if (isAuthenticated && user) {
      const from = location.state?.from?.pathname;
      navigate(from || (user.role === 'admin' ? '/admin/dashboard' : '/'), { replace: true });
    }
  }, [isAuthenticated, user, navigate, location]);

  // Clear auth error on unmount
  useEffect(() => () => clearError?.(), [clearError]);

  // Focus first input on mount
  useEffect(() => { inputRefs.current[0]?.focus(); }, []);

  // ── Input handlers ──────────────────────────────────────────────────────────
  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    if (digit && index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus();
    if (digit && index === OTP_LENGTH - 1 && newDigits.every((d) => d !== ''))
      submitCode(newDigits.join(''));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const nd = [...digits]; nd[index] = ''; setDigits(nd);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const newDigits = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((char, i) => { newDigits[i] = char; });
    setDigits(newDigits);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    if (pasted.length === OTP_LENGTH) submitCode(pasted);
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const submitCode = async (code) => {
    if (isSubmitting || authLoading) return;
    if (code.length !== OTP_LENGTH) { toast.error('Please enter all 6 digits'); return; }

    setIsSubmitting(true);
    clearError?.();

    try {
      const result = await verifyOTP(code);
      if (!result.success) {
        toast.error(result.error || 'Invalid code');
        setDigits(Array(OTP_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
        return;
      }
      toast.success(`Welcome${result.data?.full_name ? `, ${result.data.full_name}` : ''}!`);
      const from = location.state?.from?.pathname;
      navigate(from || (result.data?.role === 'admin' ? '/admin/dashboard' : '/'), { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); submitCode(digits.join('')); };

  // ── Resend ──────────────────────────────────────────────────────────────────
  const handleResend = async () => {
    if (!canResend || isResending) return;
    setIsResending(true);
    clearError?.();
    try {
      const result = await resendOTP();
      if (result.success) {
        toast.success('A new code has been sent to your email.');
        setDigits(Array(OTP_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
        restartCooldown();
      } else {
        toast.error(result.error || 'Failed to resend code');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleBack = () => { cancelOTP(); navigate('/login', { replace: true }); };

  // ── Derived ─────────────────────────────────────────────────────────────────
  const isLoading = isSubmitting || authLoading;
  const isComplete = digits.every((d) => d !== '');
  const maskedEmail = otpEmail
    ? otpEmail.replace(/(.{2})(.*)(?=@)/, (_, a, b) => a + '*'.repeat(b.length))
    : '';
  const reasonMeta = otpReason ? (REASON_META[otpReason] ?? null) : null;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex">
      {/* Left decorative panel */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1594140701076-3400bf982497?w=1200&q=80"
          alt="Jewellery Collection"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex items-center justify-center text-white text-center p-8">
          <div>
            <h2 className="font-playfair text-4xl mb-4">Secure Access</h2>
            <p className="text-lg opacity-90">One last step to protect your account</p>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          {/* Back */}
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-8 transition-colors"
            disabled={isLoading}
          >
            <ArrowLeft size={16} />
            Back to login
          </button>

          {/* Header */}
          <div className="mb-6">
            <div className="w-14 h-14 bg-antique-gold/10 rounded-full flex items-center justify-center mb-4">
              <ShieldCheck className="w-7 h-7 text-antique-gold" />
            </div>
            <h1 className="font-playfair text-3xl mb-2">Check your email</h1>
            <p className="text-gray-600">
              We sent a 6-digit code to{' '}
              <span className="font-medium text-gray-900">{maskedEmail}</span>
            </p>
          </div>

          {/* Reason banner */}
          <AnimatePresence>
            {reasonMeta && (
              <motion.div
                key="reason-banner"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className={`mb-6 p-4 rounded-lg border flex items-start gap-3 ${reasonMeta.bg} ${reasonMeta.border}`}
              >
                <reasonMeta.Icon size={18} className={`mt-0.5 flex-shrink-0 ${reasonMeta.iconColor}`} />
                <div>
                  <p className={`text-sm font-semibold ${reasonMeta.text}`}>{reasonMeta.label}</p>
                  <p className={`text-xs mt-0.5 ${reasonMeta.text} opacity-80`}>{reasonMeta.description}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Auth error */}
          <AnimatePresence>
            {authError && (
              <motion.div
                key="auth-error"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
              >
                <p className="text-sm text-red-800">{authError}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* OTP inputs */}
          <form onSubmit={handleSubmit}>
            <div className="flex gap-3 justify-center mb-8" onPaste={handlePaste}>
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={isLoading}
                  aria-label={`Digit ${index + 1}`}
                  className={`
                    w-12 h-14 text-center text-xl font-semibold rounded-lg border-2 outline-none
                    transition-all duration-150
                    ${digit
                      ? 'border-antique-gold bg-antique-gold/5 text-void-black'
                      : 'border-gray-200 bg-white text-void-black'
                    }
                    focus:border-antique-gold focus:bg-antique-gold/5
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                />
              ))}
            </div>

            <Button type="submit" disabled={!isComplete || isLoading} className="w-full btn-gold">
              {isLoading ? <span className="animate-pulse">Verifying…</span> : 'Verify & Sign In'}
            </Button>
          </form>

          {/* Resend section */}
          <div className="mt-8 text-center space-y-2">
            <p className="text-sm text-gray-500">Didn't receive the code?</p>

            {canResend ? (
              <button
                onClick={handleResend}
                disabled={isResending || isLoading}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-antique-gold
                           hover:underline disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              >
                <RefreshCw size={13} className={isResending ? 'animate-spin' : ''} />
                {isResending ? 'Sending…' : 'Resend code'}
              </button>
            ) : (
              <p className="inline-flex items-center gap-1.5 text-sm text-gray-400">
                <Clock size={13} />
                Resend available in{' '}
                <span className="font-semibold tabular-nums text-gray-600">{seconds}s</span>
              </p>
            )}

            <p className="text-xs text-gray-400">Code expires in 5 minutes</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}